/*
 *  -----------------------------------------------------------------------
 *    <copyright file="review-history-grid/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React from 'react';
import { orderBy } from '@progress/kendo-data-query';
import { REVIEW_HISTORY_GRID_COLUMNS } from '@components/central-monitoring/study-summary/review-history-grid/review-history-grid.data';
import { ReviewHistoryGridProps } from '@components/central-monitoring/study-summary/review-history-grid/review-history-grid.interface';
import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import { useTranslation } from '@hooks/index';
import './styles.scss';
import { formUpdatedCreatedField } from '@utils/filter';
import { DataSetFields, DataSetReviewFields } from '@queries/generated/fragments.generated';

const ReviewHistoryGrid = ({ dataSets, dataSetReviews, selectedDataset }: ReviewHistoryGridProps) => {
    const { t } = useTranslation();

    const getStudyReviews = (sets: DataSetFields[], reviews: DataSetReviewFields[], current: DataSetFields) => {
        const availableSets = dataSets?.filter(
            (item) => new Date(current?.dataCutDate).getTime() > new Date(item.dataCutDate).getTime(),
        );
        const setsWithReviews = availableSets.map((item) => {
            const reviewIndex = reviews.findIndex((review) => review.dataSetId === item.id);
            const set = reviewIndex !== -1 ? reviews[reviewIndex] : item;

            return {
                ...set,
                dataCutDate: item.dataCutDate,
                _updatedByString: formUpdatedCreatedField(set.updatedBy || undefined),
            };
        });

        return orderBy(setsWithReviews, [{ field: 'dataCutDate', dir: 'desc' }]);
    };

    const reviewsData = getStudyReviews(dataSets, dataSetReviews, selectedDataset);

    return (
        <ExpandableGridV2
            title={t('cm:reviewHistory')}
            customKey="review-history-grid"
            id="reviewHistoryGrid"
            items={reviewsData}
            groupable
            scrollable="scrollable"
            reorderable
            resizable
            pageable={false}
            showFirstColumn={false}
            hideCheckbox
            hideExpand
            columns={REVIEW_HISTORY_GRID_COLUMNS(t)}
        />
    );
};

export default ReviewHistoryGrid;
